import { FaPhoneAlt, FaSearch, FaTools } from "react-icons/fa";
import { useRef, useEffect, useState } from "react";

// Intersection Observer hook for reveal-on-visible animation
function useOnScreen(ref, rootMargin = "0px") {
  const [isIntersecting, setIntersecting] = useState(false);

  useEffect(() => {
    if (!ref.current) return;
    const observer = new window.IntersectionObserver(
      ([entry]) => setIntersecting(entry.isIntersecting),
      { rootMargin }
    );
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [ref, rootMargin]);

  return isIntersecting;
}

export default function AboutProcess() {
  const sectionRef = useRef(null);
  const [hasAnimated, setHasAnimated] = useState(false);

  // Trigger animation when section is visible
  const isVisible = useOnScreen(sectionRef, "-70px"); // somewhat before fully visible

  useEffect(() => {
    if (isVisible && !hasAnimated) setHasAnimated(true);
  }, [isVisible, hasAnimated]);

  // Shared animation parameters
  const initialY = 38;
  const duration = 1200;
  const baseTransition = `all ${duration}ms cubic-bezier(0.33,0.75,0.68,1)`;

  // Animation style helper
  const animStyle = (delay = 0, y = initialY) => ({
    opacity: hasAnimated ? 1 : 0,
    transform: hasAnimated ? "translateY(0px)" : `translateY(${y}px)`,
    transition: baseTransition,
    transitionDelay: hasAnimated ? `${delay}ms` : "0ms",
    willChange: "opacity, transform",
  });

  return (
    <section
      className="py-24 bg-white font-PTSerif-Regular"
      ref={sectionRef}
      style={{ position: "relative", zIndex: 1 }}
    >
      <div className="max-w-7xl mx-auto px-6 font-PTSerif-Regular">
        <div
          className="text-center mb-16 font-PTSerif-Regular"
          style={animStyle(0, 24)}
        >
          <h2 className="text-4xl font-bold text-gray-900 font-PTSerif-Regular">
            Our Service Approach
          </h2>
          <div className="w-16 h-[3px] bg-blue-500 mx-auto mt-4"></div>
        </div>

        <div className="grid md:grid-cols-3 gap-12 text-center font-PTSerif-Regular">
          {/* Step 1 */}
          <div
            className="font-PTSerif-Regular"
            style={animStyle(240, 32)}
          >
            <div className="text-blue-600 text-4xl mb-4 flex justify-center font-PTSerif-Regular">
              <FaPhoneAlt />
            </div>
            <h3 className="font-semibold text-lg mb-3 font-PTSerif-Regular">
              Service Request
            </h3>
            <p className="text-gray-600 text-sm font-PTSerif-Regular">
              Clients reach out to our support team to schedule technical
              assistance or preventive maintenance services.
            </p>
          </div>

          {/* Step 2 */}
          <div
            className="font-PTSerif-Regular"
            style={animStyle(420, 36)}
          >
            <div className="text-blue-600 text-4xl mb-4 flex justify-center font-PTSerif-Regular">
              <FaSearch />
            </div>
            <h3 className="font-semibold text-lg mb-3 font-PTSerif-Regular">
              Technical Assessment
            </h3>
            <p className="text-gray-600 text-sm font-PTSerif-Regular">
              Our engineers analyze system issues, infrastructure requirements,
              and security configurations to determine the best solution.
            </p>
          </div>

          {/* Step 3 */}
          <div
            className="font-PTSerif-Regular"
            style={animStyle(600, 40)}
          >
            <div className="text-blue-600 text-4xl mb-4 flex justify-center font-PTSerif-Regular">
              <FaTools />
            </div>
            <h3 className="font-semibold text-lg mb-3 font-PTSerif-Regular">
              Implementation & Support
            </h3>
            <p className="text-gray-600 text-sm font-PTSerif-Regular">
              Once identified, we implement the solution and provide ongoing
              technical support to maintain long-term reliability.
            </p>
          </div>
        </div>

        <div
          className="mt-20 text-center max-w-3xl mx-auto text-gray-600 leading-relaxed font-PTSerif-Regular"
          style={animStyle(860, 28)}
        >
          STRALIA PRIVATE LIMITED continues to build long-term relationships
          with businesses by providing dependable IT support, skilled technical
          expertise, and proactive system maintenance that keeps operations
          running smoothly.
        </div>
      </div>
    </section>
  );
}